import { Button, Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { abuseSubjectPath, SUBJECT_TYPE_LABELS } from '@/features/abuse/abuse-meta';
import { SubjectTypeToggles } from '@/features/abuse/subject-type-toggles';
import { type ScoringThresholds, subjectColumns } from '@/features/abuse/top-subjects/subject-columns';
import { useAbuseSubjects } from '@/hooks/queries/compliance/use-abuse';
import { getErrorMessage } from '@/lib/api/error';
import type { AbuseSubjectSort, AbuseSubjectType } from '@/services/api/abuse';
import { formatDate } from '@/utils/format';

const TOP_SUBJECTS_LIMIT = 10;

const SORT_OPTIONS: Array<{ value: AbuseSubjectSort; label: string }> = [
  { value: 'peak', label: 'Peak score' },
  { value: 'score', label: 'Current score' },
  { value: 'triggered', label: 'Recently triggered' },
];

interface TopSubjectsCardProps {
  since: string;
  thresholds: ScoringThresholds | null;
  subjectType: AbuseSubjectType;
  sort: AbuseSubjectSort;
  onSubjectTypeChange: (subjectType: AbuseSubjectType) => void;
  onSortChange: (sort: AbuseSubjectSort) => void;
}

/** Score items hold each subject's latest state, so this ranks current scores of subjects active since the window opened */
export const TopSubjectsCard = ({ since, thresholds, subjectType, sort, onSubjectTypeChange, onSortChange }: TopSubjectsCardProps) => {
  const navigate = useNavigate();
  const query = useAbuseSubjects({ subjectType, sort, since, limit: TOP_SUBJECTS_LIMIT });
  const columns = useMemo(() => subjectColumns(thresholds), [thresholds]);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <CardTitle>Top subjects</CardTitle>
          <p className="text-xs text-muted-foreground">Current scores of subjects active since {formatDate(since)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <SubjectTypeToggles label="Subject type" onChange={onSubjectTypeChange} value={subjectType} />
          <fieldset aria-label="Rank subjects by" className="m-0 flex items-center gap-1 border-0 p-0">
            {SORT_OPTIONS.map((option) => (
              <Button key={option.value} onClick={() => onSortChange(option.value)} size="sm" type="button" variant={sort === option.value ? 'primary' : 'outline'}>
                {option.label}
              </Button>
            ))}
          </fieldset>
        </div>
      </CardHeader>
      <CardContent>
        {query.isError ? (
          <QueryErrorBanner message={`Failed to fetch subjects: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            data={query.data}
            emptyMessage={`No ${SUBJECT_TYPE_LABELS[subjectType].toLowerCase()} active since ${formatDate(since)}`}
            getRowId={(row) => row.subjectId}
            isLoading={query.isFetching}
            onRowClick={(row) => navigate(abuseSubjectPath(row))}
            surface={false}
          />
        )}
      </CardContent>
    </Card>
  );
};
TopSubjectsCard.displayName = 'TopSubjectsCard';
